import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@silospot/i18n/react';
import { type PasswordResetRequestInput, passwordResetRequestSchema } from '@silospot/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

export function ForgotPasswordPage(): ReactElement {
  const { t } = useTranslation('auth');

  const form = useForm<PasswordResetRequestInput>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({ mutationFn: api.requestPasswordReset });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('forgotPassword.title')}</CardTitle>
        <CardDescription>{t('forgotPassword.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {mutation.isSuccess ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('forgotPassword.sent')}</p>
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('forgotPassword.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={mutation.isPending}>
                {t('forgotPassword.submit')}
              </Button>
              {mutation.isError ? (
                <p className="text-sm font-medium text-destructive">{t('forgotPassword.error')}</p>
              ) : null}
              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}
